import { Component } from '@theme/component';
import { CartAddEvent } from '@theme/events';
import { RoomBannerModalComponent } from '@theme/na-the-room-banner-modal';
import { calculateBundleDiscount, ceilBundleSavingsCents, readBundleDiscountSettings } from '@theme/na-bundle-discount';
import {
    formatRoomMoney,
    isCardProductEligible,
    readCardTags,
    readRoomDiscountContext,
    renderRoomCardPrice,
    resolveRoomItemDiscount,
    roomCheckoutUrl,
} from '@theme/na-room-pricing';

/**
 * @typedef {Object} ProductData
 * @property {string} id
 * @property {string} handle
 * @property {string} title
 * @property {string} featured_image
 * @property {Array<{id: number, price: number, compare_at_price: number|null, available: boolean, options: string[], option1: string|null, option2: string|null, option3: string|null, featured_image: {src: string}|null, metafield_discount?: number|null, metafield_recommended_discount?: number|null}>} variants
 * @property {string[]} options
 * @property {number} [section_discount_percent]
 * @property {number} [section_discount]
 * @property {number} [section_discount_dollars]
 * @property {string} [product_type]
 * @property {string[]} [tags]
 * @property {boolean} [is_free]
 * @property {number|null} [metafield_discount]
 * @property {number|null} [metafield_recommended_discount]
 */

/**
 * @typedef {Object} RoomRefs
 * @property {HTMLElement} cta
 * @property {HTMLElement} ctaCompare
 * @property {HTMLElement} ctaTotal
 * @property {HTMLElement[]} productCards
 * @property {HTMLElement} [ctaAlert]
 * @property {HTMLElement} [badge]
 * @property {HTMLElement} [subtotal]
 * @property {HTMLElement} [savingsRow]
 * @property {HTMLElement} [savingsAmount]
 */

/** @extends {Component<RoomRefs>} */
export class RoomInfoComponent extends Component {
    /** @type {string[]} */
    requiredRefs = ['cta', 'ctaTotal', 'productCards'];

    /** @type {AbortController|null} */
    #abortController = null;

    /** @type {Map<string, ProductData>} */
    #productsData = new Map();

    /** @type {Map<string, string>} */
    #selectedVariants = new Map();

    /** @type {number} */
    #sectionDiscountPercent = 0;

    /** @type {number} */
    #sectionDiscountValue = 0;

    /** @type {number} */
    #bundleDiscountPercent = 0;

    /** @type {boolean} */
    #useGlobalBundleDiscount = false;

    /** @type {ReturnType<typeof readBundleDiscountSettings>} */
    #bundleSettings = { twoPercent: 0, threePercent: 0, productTypes: [], productTags: [] };

    connectedCallback() {
        super.connectedCallback();
        this.#bindRoomEvents();
        this.#initProductsData();
        this.#initSectionDiscount();
        this.#calculateTotal();
    }

    updatedCallback() {
        super.updatedCallback();
        this.#bindRoomEvents();
        this.#initProductsData();
        this.#initSectionDiscount();
        this.#calculateTotal();
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this.#abortController?.abort();
        this.#productsData.clear();
        this.#selectedVariants.clear();
    }

    #bindRoomEvents() {
        this.#abortController?.abort();
        this.#abortController = new AbortController();
        const { signal } = this.#abortController;
        this.addEventListener(
            'room:variant-change',
            /** @type {EventListener} */ (this.handleVariantChange.bind(this)),
            { signal }
        );
        this.addEventListener(
            'room:product-data-update',
            /** @type {EventListener} */ (this.handleProductDataUpdate.bind(this)),
            { signal }
        );
        this.addEventListener('room:addon-change', /** @type {EventListener} */ (this.handleAddonChange.bind(this)), {
            signal,
        });
    }

    #initProductsData() {
        const productsJson = this.querySelector('#room-products-data');
        if (productsJson) {
            try {
                const data = JSON.parse(productsJson.textContent || '{}');
                Object.entries(data).forEach(([productId, product]) => {
                    this.#productsData.set(productId, product);
                });
            } catch (e) {
                console.error('Error parsing products data:', e);
            }
        }

        this.querySelectorAll('script.room-info__product-data').forEach((node) => {
            if (!(node instanceof HTMLScriptElement)) return;
            const productId = node.dataset.productId;
            if (!productId) return;

            try {
                const parsed = JSON.parse(node.textContent || '{}');
                const card = this.querySelector(`[data-product-id="${productId}"]`);
                /** @type {Partial<ProductData>} */
                const existing = this.#productsData.get(productId) || {};
                const tags = parsed.tags?.length
                    ? parsed.tags
                    : card instanceof HTMLElement
                      ? readCardTags(card)
                      : existing.tags || [];
                this.#productsData.set(productId, {
                    ...existing,
                    ...parsed,
                    id: String(productId),
                    product_type:
                        parsed.product_type ||
                        (card instanceof HTMLElement ? card.dataset.productType : '') ||
                        existing.product_type ||
                        '',
                    tags,
                });
            } catch (e) {
                console.error('Error parsing product data:', e);
            }
        });

        this.refs.productCards.forEach((card) => {
            const productId = card.dataset.productId;
            const variantId = card.dataset.selectedVariant;
            if (productId && variantId) {
                this.#selectedVariants.set(productId, variantId);
            }
        });
    }

    #initSectionDiscount() {
        this.#sectionDiscountPercent = parseFloat(this.dataset.discountPercent || '') || 0;
        this.#sectionDiscountValue = parseFloat(this.dataset.discountValue || '') || 0;
        this.#bundleDiscountPercent = parseFloat(this.dataset.bundleDiscountPercent || '0') || 0;
        this.#useGlobalBundleDiscount = this.dataset.useGlobalBundleDiscount === 'true';
        this.#bundleSettings = readBundleDiscountSettings(this);
    }

    /**
     * @param {HTMLElement} card
     * @param {{ product_type?: string, tags?: string[], is_free?: boolean }} product
     * @returns {boolean}
     */
    #isCardProductEligible(card, product) {
        return isCardProductEligible(card, product, readRoomDiscountContext(this));
    }

    /** @param {CustomEvent} event */
    handleVariantChange(event) {
        const { productId, variantId, price, comparePrice } = event.detail;
        const productCard = this.querySelector(`[data-product-id="${productId}"]`);
        if (!(productCard instanceof HTMLElement)) return;

        this.#selectedVariants.set(productId, variantId);
        productCard.dataset.selectedVariant = variantId;

        const product = this.#productsData.get(productId);
        const isFree = product?.is_free || productCard.dataset.isFree === 'true';
        const priceWrap = productCard.querySelector('.room-info__price-wrap');

        if (isFree && priceWrap) {
            renderRoomCardPrice(priceWrap, price, price, true);
        } else if (product) {
            const variant = product.variants.find((v) => String(v.id) === String(variantId));
            if (variant) {
                const isEligible = this.#isCardProductEligible(productCard, product);
                const { finalPrice, originalPrice } = this.#applyDiscountChain(
                    variant,
                    product,
                    productCard,
                    isEligible
                );
                this.#updateCardPrice(productCard, finalPrice, originalPrice);
            } else {
                this.#updateCardPrice(productCard, price, comparePrice);
            }
        } else {
            this.#updateCardPrice(productCard, price, comparePrice);
        }

        this.#calculateTotal();
    }

    handleAddonChange() {
        this.#calculateTotal();
    }

    /**
     * @returns {{ totalCents: number, compareCents: number, savingsCents: number }}
     */
    #sumSelectedAddons() {
        let totalCents = 0;
        let compareCents = 0;

        this.querySelectorAll('.room-info__addon-card.is-selected').forEach((card) => {
            if (!(card instanceof HTMLElement)) return;
            if (card.closest('[data-addons-group][hidden]')) return;

            const priceCents = parseInt(card.dataset.addonPrice || '0', 10) || 0;
            const compare = parseInt(card.dataset.addonCompare || '0', 10) || 0;
            totalCents += priceCents;
            compareCents += compare > priceCents ? compare : priceCents;
        });

        return {
            totalCents,
            compareCents,
            savingsCents: Math.max(0, compareCents - totalCents),
        };
    }

    /**
     * @returns {Array<{ id: string, quantity: number, properties: Record<string, string> }>}
     */
    #getSelectedAddonCartItems() {
        /** @type {Array<{ id: string, quantity: number, properties: Record<string, string> }>} */
        const items = [];

        this.querySelectorAll('.room-info__addon-card.is-selected').forEach((card) => {
            if (!(card instanceof HTMLElement)) return;
            if (card.closest('[data-addons-group][hidden]')) return;

            const variantId = card.dataset.addonVariantId;
            if (!variantId) return;

            items.push({
                id: variantId,
                quantity: 1,
                properties: { _addon: 'true' },
            });
        });

        return items;
    }

    /** @param {CustomEvent} event */
    handleProductDataUpdate(event) {
        const { productId, product, metafields = {} } = event.detail;
        if (!productId || !product) return;

        const existing = this.#productsData.get(String(productId));
        this.#productsData.set(String(productId), {
            ...product,
            id: String(productId),
            section_discount_percent: this.#sectionDiscountPercent,
            section_discount_dollars: existing?.section_discount_dollars || 0,
            is_free: existing?.is_free || product.is_free || false,
            metafield_discount: metafields.metafield_discount ?? product.metafield_discount ?? null,
            metafield_recommended_discount:
                metafields.metafield_recommended_discount ?? product.metafield_recommended_discount ?? null,
            tags: product.tags?.length ? product.tags : existing?.tags || [],
            product_type: product.product_type || existing?.product_type || '',
        });
    }

    /** @param {Event} event */
    handleOpenModal(event) {
        const trigger = event.target instanceof Element ? event.target.closest('[data-modal-trigger]') : null;
        const productId = trigger instanceof HTMLElement ? trigger.dataset.modalTrigger : undefined;
        if (!productId) return;
        const modal = /** @type {{ openModal?: () => void } | null} */ (this.querySelector(`#room-modal-${productId}`));
        if (modal && typeof modal.openModal === 'function') {
            modal.openModal();
        }
    }

    /**
     * @param {HTMLElement} card
     * @param {number} price
     * @param {number|null} comparePrice
     */
    #updateCardPrice(card, price, comparePrice) {
        const priceWrap = card.querySelector('.room-info__price-wrap');
        if (!priceWrap) return;
        renderRoomCardPrice(priceWrap, price, comparePrice);
    }

    #calculateTotal() {
        let totalCents = 0;
        let compareTotalCents = 0;
        let nonBundleSavingsCents = 0;
        /** @type {import('@theme/na-bundle-discount').BundleDiscountItem[]} */
        const bundleItems = [];

        this.refs.productCards.forEach((card) => {
            const productId = card.dataset.productId;
            const variantId = card.dataset.selectedVariant;
            if (!productId || !variantId) return;

            const product = this.#productsData.get(productId);
            if (!product) return;

            const variant = product.variants.find((v) => String(v.id) === String(variantId));
            if (!variant) return;

            const isFree = product.is_free || card.dataset.isFree === 'true';
            const isEligible = this.#isCardProductEligible(card, product);

            if (isFree) {
                compareTotalCents += variant.compare_at_price || variant.price;
                nonBundleSavingsCents += variant.price;
                return;
            }

            if (isEligible) {
                bundleItems.push({
                    price: variant.price,
                    compare_at_price: variant.compare_at_price,
                    product_type: product.product_type || card.dataset.productType || '',
                    tags: product.tags?.length ? product.tags : readCardTags(card),
                    is_free: false,
                });
            }

            const { finalPrice, originalPrice } = this.#applyDiscountChain(variant, product, card, isEligible);
            totalCents += finalPrice;
            compareTotalCents += originalPrice;

            if (!isEligible) {
                nonBundleSavingsCents += originalPrice - finalPrice;
            }
        });

        const addonTotals = this.#sumSelectedAddons();
        totalCents += addonTotals.totalCents;
        compareTotalCents += addonTotals.compareCents;
        nonBundleSavingsCents += addonTotals.savingsCents;

        let bundleSavingsCents = 0;
        if (this.#useGlobalBundleDiscount) {
            const bundleResult = calculateBundleDiscount(bundleItems, this.#bundleSettings, true);
            bundleSavingsCents = bundleResult.savingsCents;

            if (bundleSavingsCents <= 0 && this.#bundleDiscountPercent > 0 && bundleResult.compareCents > 0) {
                const rawSavings = Math.floor((bundleResult.compareCents * this.#bundleDiscountPercent) / 100);
                bundleSavingsCents = ceilBundleSavingsCents(rawSavings);
            }

            const serverBundleSavings = parseInt(this.dataset.bundleSavingsCents || '0', 10) || 0;
            if (bundleSavingsCents <= 0 && serverBundleSavings > 0) {
                bundleSavingsCents = serverBundleSavings;
            }
        }

        const savingsCents = nonBundleSavingsCents + bundleSavingsCents;

        if (this.#useGlobalBundleDiscount && bundleSavingsCents > 0) {
            totalCents = compareTotalCents - savingsCents;
        } else if (!this.#useGlobalBundleDiscount && this.#sectionDiscountValue > 0) {
            totalCents = Math.max(0, totalCents - this.#sectionDiscountValue * 100);
        }

        if (this.refs.ctaTotal) {
            this.refs.ctaTotal.textContent = formatRoomMoney(totalCents);
        }

        if (this.refs.ctaCompare) {
            this.refs.ctaCompare.hidden = savingsCents <= 0;
            if (savingsCents > 0) {
                this.refs.ctaCompare.textContent = formatRoomMoney(compareTotalCents);
            }
        }

        if (this.refs.subtotal) {
            this.refs.subtotal.textContent = formatRoomMoney(compareTotalCents, true);
        }
        if (this.refs.savingsRow) {
            this.refs.savingsRow.hidden = savingsCents <= 0;
        }
        if (this.refs.savingsAmount) {
            this.refs.savingsAmount.textContent = savingsCents > 0 ? `-${formatRoomMoney(savingsCents, true)}` : '';
        }

        if (this.refs.badge) {
            const template = this.dataset.discountBadge || '';
            let text = template.replace('$[savings]', '[savings]');
            if (text.includes('[savings]')) {
                text = savingsCents > 0 ? text.replace('[savings]', formatRoomMoney(savingsCents, true)) : '';
            }
            if (text.includes('[percent]')) {
                const percentToken =
                    this.#useGlobalBundleDiscount && this.#bundleDiscountPercent > 0
                        ? this.#bundleDiscountPercent
                        : this.#sectionDiscountPercent;
                text = percentToken > 0 ? text.replace('[percent]', String(percentToken)) : '';
            }
            this.refs.badge.textContent = text;
            this.refs.badge.hidden = text === '';
        }
    }

    /**
     * @param {{ price: number, compare_at_price: number|null, metafield_discount?: number|null, metafield_recommended_discount?: number|null }} variant
     * @param {{ section_discount_dollars?: number, is_free?: boolean, metafield_discount?: number|null, metafield_recommended_discount?: number|null, product_type?: string, tags?: string[] }} product
     * @param {HTMLElement} [card]
     * @param {boolean} [isEligible=false]
     * @returns {{ finalPrice: number, originalPrice: number }}
     */
    #applyDiscountChain(variant, product, card, isEligible = false) {
        const productType = product.product_type || card?.dataset.productType || '';
        const tags = product.tags?.length ? product.tags : card ? readCardTags(card) : [];

        if (!isEligible && card && this.#useGlobalBundleDiscount) {
            isEligible = this.#isCardProductEligible(card, {
                product_type: productType,
                tags,
                is_free: product.is_free,
            });
        }

        const resolved = resolveRoomItemDiscount({
            price: variant.price,
            compareAt: variant.compare_at_price || 0,
            isFree: Boolean(product.is_free),
            isEligible,
            useGlobalBundle: this.#useGlobalBundleDiscount,
            bundlePercent: this.#bundleDiscountPercent,
            sectionPercent: this.#sectionDiscountPercent,
            sectionDollars: product.section_discount_dollars || 0,
            variantMetaDiscount: variant.metafield_discount,
            productMetaDiscount: product.metafield_discount,
            variantRecDiscount: variant.metafield_recommended_discount,
            productRecDiscount: product.metafield_recommended_discount,
        });

        return { finalPrice: resolved.finalPrice, originalPrice: resolved.originalPrice };
    }

    /** @param {Event} event */
    async handleAddToCart(event) {
        event.preventDefault();

        const goToCheckout = this.dataset.ctaAction === 'checkout';
        const checkoutUrl = roomCheckoutUrl();
        const ctaButton = /** @type {HTMLButtonElement} */ (this.refs.cta);
        const ctaAlert = this.refs.ctaAlert;
        if (ctaAlert instanceof HTMLElement) ctaAlert.textContent = '';
        ctaButton.disabled = true;
        ctaButton.setAttribute('aria-busy', 'true');

        const items = [];
        this.refs.productCards.forEach((card) => {
            const productId = card.dataset.productId;
            const variantId = card.dataset.selectedVariant;
            if (productId && variantId) {
                items.push({ id: variantId, quantity: 1 });
            }
        });

        items.push(...this.#getSelectedAddonCartItems());

        if (items.length === 0) {
            if (goToCheckout) {
                window.location.href = checkoutUrl;
            } else {
                this.#openCartDrawer();
            }
            ctaButton.disabled = false;
            ctaButton.removeAttribute('aria-busy');
            return;
        }

        try {
            const cartResponse = await fetch(`${Theme.routes.cart_url}.js`);
            if (!cartResponse.ok) throw new Error('Failed to fetch cart');
            const cartData = await cartResponse.json();
            const existingVariantIds = new Set(
                cartData.items.map((/** @type {{variant_id:number}} */ i) => i.variant_id)
            );
            const itemsToAdd = items.filter((item) => !existingVariantIds.has(Number(item.id)));

            if (itemsToAdd.length === 0) {
                if (goToCheckout) {
                    window.location.href = checkoutUrl;
                } else {
                    this.#openCartDrawer();
                }
                return;
            }

            const sectionIds = /** @type {string[]} */ ([]);
            if (!goToCheckout) {
                document.querySelectorAll('cart-items-component').forEach((el) => {
                    if (el instanceof HTMLElement && el.dataset.sectionId) {
                        sectionIds.push(el.dataset.sectionId);
                    }
                });
            }

            const addResponse = await fetch(Theme.routes.cart_add_url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({
                    items: itemsToAdd,
                    ...(sectionIds.length ? { sections: sectionIds, sections_url: window.location.pathname } : {}),
                }),
            });

            if (!addResponse.ok) {
                const err = await addResponse.json().catch(() => ({}));
                throw new Error(err.description || err.message || 'Failed to add items to cart');
            }

            const data = await addResponse.json();

            if (goToCheckout) {
                window.location.href = checkoutUrl;
                return;
            }

            this.dispatchEvent(
                new CartAddEvent({}, this.id, {
                    source: 'room-info-component',
                    itemCount: itemsToAdd.length,
                    sections: data.sections,
                })
            );

            this.#openCartDrawer();
        } catch (error) {
            console.error('Error adding to cart:', error);
            if (ctaAlert instanceof HTMLElement) {
                ctaAlert.textContent = this.dataset.addError || '';
            }
        } finally {
            ctaButton.disabled = false;
            ctaButton.removeAttribute('aria-busy');
        }
    }

    #openCartDrawer() {
        const cartDrawer = /** @type {any} */ (document.querySelector('cart-drawer-component'));
        if (cartDrawer && typeof cartDrawer.open === 'function') {
            cartDrawer.open();
        }
    }
}

export class RoomProductModalComponent extends RoomBannerModalComponent {
    connectedCallback() {
        super.connectedCallback();
        this.addEventListener(CartAddEvent.eventName, this.#onCartAdd);
    }

    disconnectedCallback() {
        this.removeEventListener(CartAddEvent.eventName, this.#onCartAdd);
        super.disconnectedCallback();
    }

    /**
     * @param {CartAddEvent} event
     */
    #onCartAdd = (event) => {
        if (event.detail?.data?.didError) return;
        if (event.detail?.data?.source !== 'product-form-component') return;
        this.closeModal();
    };
}

if (!customElements.get('room-info-component')) {
    customElements.define('room-info-component', RoomInfoComponent);
}

if (!customElements.get('room-product-modal-component')) {
    customElements.define('room-product-modal-component', RoomProductModalComponent);
}
